import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { getMsalInstance } from "./msalSingleton";
import { loginRequest } from "../authConfig";
import { getDevSession } from "./devSession";
import { binnenInterval } from './laadStatus';

/**
 * Check if a JWT token is expired or about to expire (within 5 minutes).
 */
export function isTokenExpired(token, bufferSeconds = 300) {
  try {
    // JWT = base64url: '-' en '_' laat atob struikelen (→ "verlopen" → elke call een refresh).
    const deel = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(deel.padEnd(deel.length + ((4 - (deel.length % 4)) % 4), '=')));
    const expiresAt = payload.exp * 1000; // Convert to ms
    return Date.now() >= expiresAt - bufferSeconds * 1000;
  } catch {
    return true; // If we can't parse it, treat as expired
  }
}

/**
 * Get Authorization headers with Bearer token for API calls.
 * If the token is expired, tries a popup login instead of failing silently.
 * Returns { Authorization: "Bearer ..." } if token available, otherwise {}.
 *
 * Gelijktijdige aanroepen (een pagina start er ~15 tegelijk) delen één lopende opvraag:
 * bij een bijna verlopen ID-token is dat één verversing en hooguit één popup, niet vijftien.
 */
// Delen gebeurt per identiteit (dev-sessie of MSAL-account, op homeAccountId): wisselt het
// account terwijl er een opvraag loopt, dan krijgt het nieuwe account nooit het token van
// het vorige.
function identiteit() {
  const dev = getDevSession();
  if (dev?.token) return `dev:${dev.token}`;
  const msal = getMsalInstance();
  const alle = msal?.getAllAccounts?.() || [];
  const acc = msal?.getActiveAccount?.() || (alle.length === 1 ? alle[0] : null);
  return `msal:${acc?.homeAccountId || ''}`;
}

const lopend = new Map(); // identiteit → lopende opvraag
export async function getAuthHeaders() {
  const wie = identiteit();
  let p = lopend.get(wie);
  if (!p) {
    // `stand.voor`: voor welke identiteit het antwoord geldt. De eigen fallback naar een ander
    // cache-account van dezelfde gebruiker verschuift die; dat is geen externe wissel.
    const stand = { voor: wie };
    p = haalAuthHeaders(stand).then((h) => ({ h, voor: stand.voor }))
      .finally(() => { if (lopend.get(wie) === p) lopend.delete(wie); });
    lopend.set(wie, p);
  }
  const { h, voor } = await p;
  // Intussen door iemand anders van account gewisseld? Dan dit antwoord niet gebruiken.
  if (identiteit() !== voor) return getAuthHeaders();
  return { ...h }; // eigen kopie per aanroeper
}

async function haalAuthHeaders(stand) {
  // Accountwissel tijdens deze opvraag: stoppen vóór elke volgende stap (geen popup of
  // verversing meer voor het vorige account). getAuthHeaders vraagt dan opnieuw.
  const verouderd = () => identiteit() !== stand.voor;
  try {
    // TEST/DEV ONLY: when a dev-login session is active, send its token instead
    // of an MSAL token. getDevSession() returns null on the production host.
    const dev = getDevSession();
    if (dev?.token) {
      return { Authorization: `Bearer ${dev.token}` };
    }

    const msalInstance = getMsalInstance();
    if (!msalInstance) return {};
    const allAccounts = msalInstance.getAllAccounts() || [];
    // Zonder actief account alleen terugvallen op een cached account als dat er
    // precies één is. Bij meerdere accounts (gedeelde edit-machine) NOOIT stilzwijgend
    // iemand kiezen — dan is opnieuw inloggen de juiste route, anders werk je
    // ongemerkt onder de identiteit van een collega.
    let account = msalInstance.getActiveAccount() || (allAccounts.length === 1 ? allAccounts[0] : null);
    if (!account) return {};

    let response;
    try {
      response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
    } catch (err) {
      if (verouderd()) return {};
      if (err instanceof InteractionRequiredAuthError) {
        // Bij méér dan één account (bv. hetzelfde account dubbel gecached na herinlog)
        // kan het actieve account niet meer silent verversen → dat gaf een login-popup
        // bij bijna élke handeling. Probeer daarom eerst de ANDERE accounts silent;
        // lukt dat, maak dát het actieve account (geen popup nodig).
        // VEILIGHEID: alleen accounts van DEZELFDE gebruiker (zelfde e-mail) — nooit
        // stilzwijgend wisselen naar het account van een collega op een gedeelde machine.
        // Zonder gebruikersnaam is "dezelfde gebruiker" niet vast te stellen: geen fallback.
        const currentUsername = (account.username || '').toLowerCase();
        for (const acc of (currentUsername ? allAccounts : [])) {
          if (acc.homeAccountId === account.homeAccountId) continue;
          if ((acc.username || '').toLowerCase() !== currentUsername) continue;
          try {
            response = await msalInstance.acquireTokenSilent({ ...loginRequest, account: acc });
            account = acc;
            break;
          } catch { /* volgende account proberen */ }
        }
        // Pas als geen enkel account silent werkt: popup (zodat andere MS-tabs blijven werken).
        if (!response) {
          if (verouderd()) return {};
          response = await msalInstance.acquireTokenPopup({ ...loginRequest, account });
        }
      } else {
        return {};
      }
    }
    // Houd het werkende account voortaan als het actieve — voorkomt herhaalde popups.
    // …maar niet als intussen een ander account actief werd (dan dit antwoord niet gebruiken).
    if (verouderd()) return {};
    if (response?.account) {
      try { msalInstance.setActiveAccount(response.account); } catch { /* ignore */ }
      stand.voor = identiteit(); // eigen wissel (fallback): het antwoord geldt nu hiervoor
    }

    // Use idToken (not accessToken) — ID tokens are meant for YOUR app (aud=clientId).
    // Access tokens are for Graph API and can't be validated by third parties.
    if (!response.idToken) return {};

    // MSAL can return a cached but expired idToken even after acquireTokenSilent.
    // If the idToken is expired or about to expire, force a fresh token via forceRefresh.
    if (isTokenExpired(response.idToken)) {
      if (verouderd()) return {};
      console.warn('[Auth] ID token expired/expiring, forcing refresh...');
      try {
        response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh: true,
        });
      } catch (refreshErr) {
        if (refreshErr instanceof InteractionRequiredAuthError) {
          if (verouderd()) return {};
          response = await msalInstance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
        } else {
          console.error('[Auth] Force refresh failed:', refreshErr.message);
          return {};
        }
      }
      if (!response.idToken || isTokenExpired(response.idToken, 0)) {
        console.error('[Auth] Still expired after force refresh');
        return {};
      }
    }

    return { Authorization: `Bearer ${response.idToken}` };
  } catch {
    return {};
  }
}

/**
 * Wrapper around fetch() that automatically adds Bearer token.
 * Drop-in replacement: authFetch(url, options) works like fetch(url, options).
 */
export async function authFetch(url, options = {}) {
  // Vóór de eerste await vastleggen: start dit verzoek in een poll (setInterval), dan telt het
  // niet mee in de laadbalk (utils/laadStatus). Na de await is die context weg.
  const stil = options.stil ?? binnenInterval();
  const authHeaders = await getAuthHeaders();
  const opts = { ...options, stil, headers: { ...authHeaders, ...(options.headers || {}) } };
  delete opts.retry; // custom veld — hoort niet in de native fetch RequestInit
  // Retry op NETWERK-fouten ("Failed to fetch", fetch()-reject) bij cold start/revisie-swap.
  // ALLEEN veilige/idempotente methodes (GET/HEAD) worden standaard herhaald: bij een mutatie
  // kan de server de request al verwerkt hebben terwijl de response wegviel — dan zou een
  // replay dubbel aanmaken. Muterende callers kunnen expliciet {retry:true} zetten als hun
  // endpoint idempotent is. Opt-out voor GET: {retry:false}.
  const method = String(opts.method || 'GET').toUpperCase();
  const isSafe = method === 'GET' || method === 'HEAD';
  const wantRetry = options.retry === true || (isSafe && options.retry !== false);
  const maxAttempts = wantRetry ? 3 : 1;
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fetch(url, opts);
    } catch (err) {
      if (err && err.name === 'AbortError') throw err; // bewuste abort nooit retryen
      lastErr = err;
      if (attempt === maxAttempts) break;
      await new Promise((r) => setTimeout(r, 250 * Math.pow(3, attempt - 1))); // 250ms, 750ms
    }
  }
  throw lastErr;
}
